import { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../assets/Logo';
import './Navbar.scss';

const navLinks = [
  { label: 'How It Works', to: '/how-it-works' },
  { label: 'Who We Serve', to: '/who-we-serve' },
  { label: 'About', to: '/about' },
];

export default function Navbar({ dark = false }) {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const isHome = pathname === '/';
  const atTop = !scrolled;
  // Home: transparent bar + black logo at top; other dark pages: solid bar + white logo at top
  const homeAtTop = isHome && dark && atTop;
  const solidDarkAtTop = dark && atTop && !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Dark text on bar everywhere except solid dark (e.g. About) at top
  const isLight = !solidDarkAtTop;
  const navClass = [
    'navbar',
    dark ? 'dark' : '',
    homeAtTop ? 'navbar--home-top' : '',
    solidDarkAtTop ? 'navbar--solid-dark' : '',
    scrolled ? 'scrolled' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <motion.nav
        className={navClass}
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="navbar__inner">
          <Link to="/" className="navbar__logo" onClick={() => setOpen(false)}>
            <Logo inverted={solidDarkAtTop} height={40} />
          </Link>

          <div className="navbar__links">
            {navLinks.map(({ label, to }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  ['navbar__link', !isLight ? 'navbar__link--dark' : '', isActive ? 'active' : ''].filter(Boolean).join(' ')
                }
              >
                {label}
              </NavLink>
            ))}
          </div>

          <button
            className={`navbar__hamburger${open ? ' open' : ''}`}
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => setOpen(v => !v)}
          >
            {[0,1,2].map(i => (
              <span
                key={i}
                className={`navbar__bar${!isLight && !open ? ' navbar__bar--light' : ''}`}
              />
            ))}
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {open && (
          <motion.div
            className={`mobile-menu open${!isLight ? ' mobile-menu--dark' : ''}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {navLinks.map(({ label, to }, i) => (
              <motion.div
                key={to}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.04 + i * 0.07 }}
              >
                <NavLink
                  to={to}
                  className={({ isActive }) =>
                    ['mobile-menu__link', !isLight ? 'mobile-menu__link--dark' : '', isActive ? 'active' : ''].filter(Boolean).join(' ')
                  }
                  onClick={() => setOpen(false)}
                >
                  {label}
                </NavLink>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <NavLink
                to="/contact"
                className={({ isActive }) =>
                  ['mobile-menu__link', !isLight ? 'mobile-menu__link--dark' : '', isActive ? 'active' : ''].filter(Boolean).join(' ')
                }
                onClick={() => setOpen(false)}
              >
                Contact
              </NavLink>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
